import { useContext, useMemo } from "react";
import { useStyles } from "./DetailsReport.styles";
import { MainTable } from "components";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { MillDetailsReportContext } from "contexts/MillDetailsReportContext/MillDetailsReportContext";
import { useParams } from "react-router-dom";
import { useDetailsReportColumns } from "./hooks/useDetailsReportColumns";
import DetailsReportFilters from "./DetailsReportFilters/DetailsReportFilters.view";
import moment from "moment";
import { DetailsReportColumnsIdEnum, IDetailsReportRow } from "./hooks/useDetailsReportColumnsInterfaces";
import { useTranslation } from "react-i18next";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { useTableOperations } from "components/MainTable/hooks/useTableOperations";
import { Order } from "components/MainTable/components/EnhancedTableHead/EnhancedTableHeadInterfaces";
import { LocationsContext } from "contexts/LocationsContext/LocationsContext";
import { handleNameTranslation, translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";

export const DetailsReport = () => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { millId } = useParams();
    const millIdMemoized = useMemo(() => (Boolean(millId) ? Number(millId) : 0), [millId]);
    const { showFilters, filters } = useContext(MillDetailsReportContext);
    const { handleRequestSort, handleChangeRowsPerPage, handleChangePage, order, orderBy, page, rowsPerPage } =
        useTableOperations();
    const { millTimezone } = useContext(LocationsContext);

    const { reportRangeEndDate: endDate, reportRangeStartDate: startDate } = useContext(MillDashboardContext);

    const queryFilters = useMemo(() => ({ ...filters, startDate, endDate }), [filters, startDate, endDate]);
    const queryTableOperationFilters = useMemo(
        () => ({
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            ...(orderBy === DetailsReportColumnsIdEnum.COMMENTS && order === "asc"
                ? { order: "asc_nulls_first" as Order }
                : orderBy === DetailsReportColumnsIdEnum.COMMENTS && order === "desc"
                ? { order: "desc_nulls_last" as Order }
                : { order }),
            orderBy,
        }),
        [page, rowsPerPage, order, orderBy],
    );

    const { loading, apolloQueryError, eventReportsDetails, totalCount, refetch } =
        EventReportsEntity.GetEventReportsDetailsBO(millIdMemoized, queryFilters, queryTableOperationFilters);

    const eventReportsFormattedTable: IDetailsReportRow[] = useMemo(() => {
        const formattedEvents = eventReportsDetails.map((event) => {
            const uniqueShifts = event.event_report_downtime_details.reduce((acc, item) => {
                const shift = item.shift_day.shift_historic.shift;
                const shiftInfo = { shift_id: shift.shift_id, name: shift.name };

                if (!acc.some((s) => s.shift_id === shiftInfo.shift_id && s.name === shiftInfo.name)) {
                    acc.push(shiftInfo);
                }

                return acc;
            }, [] as { shift_id: number; name: string }[]);

            const shiftNamesString = uniqueShifts
                .map((shift) => shift.name)
                .filter((name, index, self) => self.indexOf(name) === index)
                .join(", ");

            return {
                event_information: event,
                [DetailsReportColumnsIdEnum.DATE_STOP]: event.event_report_downtime_details[0].start_date,
                [DetailsReportColumnsIdEnum.TIME_STOP]: millTimezone
                    ? moment.utc(event.event_report_downtime_details[0].start_date).tz(millTimezone).format("h:mm A")
                    : moment.utc(event.event_report_downtime_details[0].start_date).format("h:mm A"),
                [DetailsReportColumnsIdEnum.SHIFT]: translateDefaultEntity(
                    i18n.language,
                    event?.event_report_downtime_details[0]?.shift_day.shift_historic.shift,
                    "name",
                    DefaultTranslationFieldsInTable.DAY,
                ),
                shift_info: event?.event_report_downtime_details[0]?.shift_day.shift_historic,
                multiple_shifts: uniqueShifts.length > 1 && !!event.end_date,
                multiple_shifts_names: shiftNamesString,
                [DetailsReportColumnsIdEnum.DURATION]: event?.end_date
                    ? event?.event_report_downtime_details.reduce((acc, obj) => acc + obj.valid_time, 0)
                    : t("locations.secondary_code_requirements_table.to_be_defined"),
                [DetailsReportColumnsIdEnum.PRODUCTION_LINE]: event?.machine_center.production_line?.name,
                [DetailsReportColumnsIdEnum.MACHINE_CENTER]: event.machine_center?.name,
                [DetailsReportColumnsIdEnum.FUNCTIONAL_LOCATION]: event?.functional_location
                    ? handleNameTranslation(i18n.language, event.functional_location?.translation.translation_names)
                    : undefined,
                [DetailsReportColumnsIdEnum.AREA]: event?.area
                    ? handleNameTranslation(i18n.language, event.area.translation.translation_names)
                    : undefined,
                [DetailsReportColumnsIdEnum.FAILURE_TYPE]: translateDefaultEntity(
                    i18n.language,
                    event.failure_type,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_TYPE,
                ),
                [DetailsReportColumnsIdEnum.FAILURE_CODE]: event?.failure_code
                    ? translateDefaultEntity(
                          i18n.language,
                          event.failure_code,
                          "name",
                          DefaultTranslationFieldsInTable.FAILURE_CODE,
                      )
                    : "",
                [DetailsReportColumnsIdEnum.RCA]: event.rca_event_report?.rca_id
                    ? t("locations.mill_dashboards.details.affirmative")
                    : t("locations.mill_dashboards.details.negative"),
                [DetailsReportColumnsIdEnum.OPERATOR]: `${event?.user_operator?.first_name} ${event?.user_operator?.last_name}`,
                [DetailsReportColumnsIdEnum.COMMENTS]: event.notes,
                ...(event.event_report_downtime_details.length > 1 && !!event.end_date
                    ? {
                          expandedData: event.event_report_downtime_details.map((downtimeDetails) => ({
                              start_date: downtimeDetails.start_date,
                              valid_time: downtimeDetails.valid_time,
                              shift_info: downtimeDetails.shift_day.shift_historic,
                              shift: translateDefaultEntity(
                                  i18n.language,
                                  downtimeDetails.shift_day.shift_historic.shift,
                                  "name",
                                  DefaultTranslationFieldsInTable.DAY,
                              ),
                          })),
                      }
                    : {}),
            };
        });

        return formattedEvents;
    }, [eventReportsDetails, millTimezone, t, i18n.language]);

    const tableColumns = useDetailsReportColumns(refetch) as unknown as IMainTableColumn[];

    return (
        <>
            {showFilters && <DetailsReportFilters />}
            <div className={classes.mainWrapper}>
                <MainTable
                    tableExpandedRowCustomStyle={classes.contentTableExpandedRow}
                    columns={tableColumns}
                    rows={!loading && !apolloQueryError ? eventReportsFormattedTable : []}
                    loading={loading}
                    pagination
                    count={totalCount}
                    tableOperationsValues={{ order, orderBy, page, rowsPerPage }}
                    handleRequestSortParent={handleRequestSort}
                    handleChangeRowsPerPageParent={handleChangeRowsPerPage}
                    handleChangePageParent={handleChangePage}
                    querySorted={orderBy !== DetailsReportColumnsIdEnum.DURATION}
                />
            </div>
        </>
    );
};
